import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import logger from "@/lib/logger";

const BackendHealthContext = createContext();

// Retry configuration
const RETRY_INTERVALS = [1000, 2000, 4000, 8000, 8000, 8000]; // ms
const MAX_ATTEMPTS = 6;
const REQUEST_TIMEOUT = 5000; // 5s per attempt

// Backend status states
export const BACKEND_STATUS = {
  INITIALIZING: "initializing",
  CONNECTING: "connecting",
  READY: "ready",
  DEGRADED: "degraded",
  FAILED: "failed",
};

/**
 * BackendHealthProvider - Monitors backend health and handles cold starts
 * Implements exponential backoff retry with smart status detection
 */
export function BackendHealthProvider({ children }) {
  const [backendStatus, setBackendStatus] = useState(
    BACKEND_STATUS.INITIALIZING,
  );
  const [healthCheckAttempt, setHealthCheckAttempt] = useState(0);
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(0);
  const [lastCheckTime, setLastCheckTime] = useState(null);

  /**
   * Check backend health with timeout
   */
  const checkHealth = useCallback(async () => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
    const healthEndpoint = `${backendUrl}/api/health`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(healthEndpoint, {
        method: "GET",
        signal: controller.signal,
        headers: {
          Accept: "application/json",
        },
      });

      clearTimeout(timeout);

      if (response.ok) {
        const data = await response.json();
        logger.info("Backend health check passed:", data);
        return { success: true, data };
      } else {
        logger.warn(
          "Backend health check failed with status:",
          response.status,
        );
        return { success: false, status: response.status };
      }
    } catch (error) {
      clearTimeout(timeout);

      if (error.name === "AbortError") {
        logger.warn("Backend health check timed out");
        return { success: false, error: "timeout" };
      }

      logger.error("Backend health check error:", error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Retry logic with exponential backoff
   */
  const retryTimeoutRef = useRef(null);
  const performHealthCheckRef = useRef(null);

  const performHealthCheck = useCallback(
    async (attemptNumber = 1) => {
      // Clear any existing timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      setHealthCheckAttempt(attemptNumber);
      setBackendStatus(BACKEND_STATUS.CONNECTING);
      setLastCheckTime(Date.now());

      logger.info(
        `Backend health check attempt ${attemptNumber}/${MAX_ATTEMPTS}`,
      );

      const result = await checkHealth();

      if (result.success) {
        // Success - backend is ready
        setBackendStatus(BACKEND_STATUS.READY);
        setEstimatedWaitTime(0);
        logger.info("Backend is ready!");
        return;
      }

      // Failed attempt
      if (attemptNumber >= MAX_ATTEMPTS) {
        // Max attempts reached - mark as failed
        setBackendStatus(BACKEND_STATUS.FAILED);
        logger.error("Backend health check failed after max attempts");
        return;
      }

      // Schedule retry with exponential backoff
      const nextAttempt = attemptNumber + 1;
      const delay =
        RETRY_INTERVALS[attemptNumber - 1] ||
        RETRY_INTERVALS[RETRY_INTERVALS.length - 1];

      // Calculate estimated wait time (remaining attempts)
      const remainingTime = RETRY_INTERVALS.slice(attemptNumber).reduce(
        (sum, interval) => sum + interval,
        0,
      );
      setEstimatedWaitTime(remainingTime);

      // Mark as degraded if taking too long
      if (attemptNumber >= 3) {
        setBackendStatus(BACKEND_STATUS.DEGRADED);
      }

      logger.info(`Retrying health check in ${delay}ms...`);

      // Schedule next attempt
      retryTimeoutRef.current = setTimeout(() => {
        performHealthCheckRef.current?.(nextAttempt);
      }, delay);
    },
    [checkHealth],
  );

  // Update ref to latest function in an effect
  useEffect(() => {
    performHealthCheckRef.current = performHealthCheck;
  }, [performHealthCheck]);

  /**
   * Manual retry function for user-triggered retries
   */
  const retry = useCallback(() => {
    logger.info("Manual retry triggered by user");
    setHealthCheckAttempt(0);
    setBackendStatus(BACKEND_STATUS.INITIALIZING);
    performHealthCheck(1);
  }, [performHealthCheck]);

  /**
   * Initialize health check on mount
   */
  useEffect(() => {
    // Delay initial check to avoid setState during render
    const timer = setTimeout(() => {
      performHealthCheck(1);
    }, 0);
    return () => clearTimeout(timer);
  }, [performHealthCheck]);

  /**
   * Calculate progress percentage
   */
  const progress =
    healthCheckAttempt > 0 ? (healthCheckAttempt / MAX_ATTEMPTS) * 100 : 0;

  const value = {
    backendStatus,
    healthCheckAttempt,
    estimatedWaitTime,
    lastCheckTime,
    progress,
    retry,
    isReady: backendStatus === BACKEND_STATUS.READY,
    isLoading: [
      BACKEND_STATUS.INITIALIZING,
      BACKEND_STATUS.CONNECTING,
      BACKEND_STATUS.DEGRADED,
    ].includes(backendStatus),
    isFailed: backendStatus === BACKEND_STATUS.FAILED,
  };

  return (
    <BackendHealthContext.Provider value={value}>
      {children}
    </BackendHealthContext.Provider>
  );
}

/**
 * Hook to access backend health context
 */
export function useBackendHealth() {
  const context = useContext(BackendHealthContext);
  if (!context) {
    throw new Error(
      "useBackendHealth must be used within a BackendHealthProvider",
    );
  }
  return context;
}

export default BackendHealthContext;
